import type { View, ViewContext } from '../router';
import { el, ensureStyle } from '../dom';
import { icon } from '../components/icons';
import {
  backBtn,
  emptyFocus,
  exitButton,
  ensureQuizStyle,
  fillProgress,
  focusSetup,
  focusTag,
  keyLegend,
  lengthField,
  etymologyLine,
  markChoices,
  newBirdsField,
  missedList,
  quizHeader,
  quizKeys,
  retryTag,
  afterBar,
  compareButton,
  dontKnowButton,
  undoButton,
  segmented,
  tagSlot,
  type HearHandle,
  type KeyActions,
  type Miss,
} from '../components/quiz-chrome';
import {
  answerText,
  buildNamesSession,
  choiceText,
  promptText,
  type DirectionMode,
  type NameMode,
  type NameQuestion,
} from '../../core/quiz/names';
import { focusCodes, parseFocus } from '../../core/quiz/focus';
import { buildNamesPairSession } from '../../core/quiz/pairs';
import { reaskName } from '../../core/quiz/retry';
import { isFakeName } from '../../core/quiz/fake-names';
import { getSpecies } from '../../core/region';
import { Rating, ratingFromCorrect } from '../../core/fsrs';
import { getSetting, setSetting } from '../../core/db';
import {
  newBirdShare,
  retryMisses,
  roundLength,
  setNewBirdShare,
  ROUND_LENGTHS,
  setRoundLength,
} from '../../core/quiz/settings';
import { XP_PER_CORRECT } from '../../core/progress';
import { clearUndo, gradeAnswer, undoAnswer } from '../../core/undo';
import { haptic } from '../haptics';
import { t } from '../../strings';

ensureQuizStyle();
ensureStyle(
  'quiz-names-css',
  `
  .names .card { padding:32px 22px; text-align:center;
    display:flex; flex-direction:column; gap:14px; justify-content:center; min-height:156px; }
  .names .card .front { font-family:var(--font-display);
    font-variation-settings:var(--fvs-display); font-size:27px; line-height:1.2; }
  .names .card .front.sci { font-style:italic; }
  /* A hand-drawn divider between prompt and answer, not a hairline. */
  .names .card .rule { color:var(--ink); align-self:center; width:110px; }
  .names .card .back { font-family:var(--font-display); font-size:24px; line-height:1.2;
    color:var(--moss); }
  .names .card .back.sci { font-style:italic; }
  .names .reveal { width:100%; padding:16px; font-size:17px; }
  .names .grades { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin-top:auto; }
  .names .grades button { padding:14px 4px; font-size:15px; }
  .names .grades button.again { background:var(--danger); color:var(--on-danger);
    border-color:transparent; }
  .names .grades button.easy { background:var(--moss); color:var(--on-dark);
    border-color:var(--honey-soft); }
  /* Name Match options carry a full name rather than a name + subtitle, so they
     get display-sized type instead of the Sound ID choice's stacked pair. */
  .names .choice .nm { font-size:20px; line-height:1.2; }
  .names .choice .nm.it { font-style:italic; }
  /* Revealed under a wrong pick: the bird the user actually named. */
  .names .choice .was { font-family:var(--font-body); font-size:14px; margin-top:5px;
    opacity:.92; display:block; }
  .names .choice .was.it { font-style:italic; }
  .names .done .next { display:flex; width:100%; max-width:280px; margin:14px auto 0; }
`,
);

const DIRECTIONS: { value: DirectionMode; label: string; note: string }[] = [
  { value: 'mixed', label: t('quiz.round.mixed'), note: t('quiz.round.mixedNote') },
  { value: 'toSci', label: t('quiz.names.direction.toSci'), note: t('quiz.names.direction.toSciNote') },
  {
    value: 'toCommon',
    label: t('quiz.names.direction.toCommon'),
    note: t('quiz.names.direction.toCommonNote'),
  },
];

const MODES: { value: NameMode; label: string; note: string }[] = [
  { value: 'choice', label: t('quiz.names.format.choice'), note: t('quiz.names.format.choiceNote') },
  { value: 'flip', label: t('quiz.names.format.flip'), note: t('quiz.names.format.flipNote') },
];

const GRADES: { rating: Rating; label: string; cls: string }[] = [
  { rating: Rating.Again, label: t('quiz.names.grade.again'), cls: 'again' },
  { rating: Rating.Hard, label: t('quiz.names.grade.hard'), cls: 'hard' },
  { rating: Rating.Good, label: t('quiz.names.grade.good'), cls: 'good' },
  { rating: Rating.Easy, label: t('quiz.names.grade.easy'), cls: 'easy' },
];

export const quizNamesView: View = async (root: HTMLElement, ctx: ViewContext) => {
  const [savedDir, savedMode, savedLen, savedShare] = await Promise.all([
    getSetting<DirectionMode>('names.direction'),
    getSetting<NameMode>('names.mode'),
    roundLength('names'),
    newBirdShare('names'),
  ]);

  let direction: DirectionMode = DIRECTIONS.some((d) => d.value === savedDir)
    ? (savedDir as DirectionMode)
    : 'mixed';
  let mode: NameMode = MODES.some((m) => m.value === savedMode)
    ? (savedMode as NameMode)
    : 'choice';
  // How many birds this round asks about. Every setting on this screen opens on
  // whatever the last Name Match round was run with.
  let len = savedLen;
  // How much of the round is held for birds this learner has never met, as a
  // share of its length; see core/quiz/settings.ts.
  let share = savedShare;

  // The pair drill: the round is built from the birds this learner has actually
  // been calling each other, not from the schedule. See core/quiz/pairs.ts.
  const drill = ctx.params.drill === 'pairs';
  // A round started from the Library is confined to the birds that were on
  // screen when its button was pressed — see core/quiz/focus.ts. The drill has
  // a pool of its own and the two never combine. The focus lives in the hash
  // and nowhere else, so a reload keeps it and leaving the round drops it.
  const focus = drill ? null : parseFocus(ctx.params.focus);
  const pool = focus ? focusCodes(focus) : null;

  const wrap = el('div.quiz.names', {});
  root.append(wrap);

  let session: NameQuestion[] = [];
  // How many questions the round was dealt. Anything past it is a miss come
  // back for another go — see `retryMisses` — and is practice, not scored.
  let size = 0;
  let index = 0;
  let retryOn = false;
  // Whether the answer behind the undo button sent its question to the back.
  let requeued = false;
  let scored = 0;
  // The species this round got wrong — in flip mode, the ones graded Again.
  let missed: Miss[] = [];
  // Whether the answer standing behind the undo button was a right one, or null
  // when there is nothing to take back. In flip mode this outlives the question
  // it belongs to — see renderFlip.
  let graded: boolean | null = null;
  let disposed = false;

  // One key listener for the whole visit, reading whatever the screen currently
  // offers. A listener added per render is a listener leaked per render.
  let keys: KeyActions | null = null;
  const releaseKeys = quizKeys(wrap, () => keys);

  // The speakers on the end-of-round Missed panel — the only sound this mode
  // makes, and the only thing in it that has to be silenced on the way out.
  let missSpeakers: HearHandle[] = [];

  function hush(): void {
    for (const s of missSpeakers) s.stop();
    missSpeakers = [];
  }

  // Only the first round of a visit inherits the daily card's hand-off.
  let firstRound = true;

  // `?go=1` — a round started from the Home card, which already knows what the
  // last one was set to. The setup screen is three taps between a learner and
  // the round they have already asked for; "Change options" at the end of the
  // round is the way back to one.
  if (ctx.params.go) void start();
  else renderSetup();

  /** What the round about to start is, in the length currently chosen. */
  function blurbText(): string {
    if (drill) {
      return t('quiz.names.setup.drillBlurb', { n: len });
    }
    // An ordinary round gets no line at all. It used to promise "N species from
    // your review queue, drawn by the same schedule as <other mode>", which read
    // as a technical note about the scheduler on the one screen whose job is to
    // start a round — the title, the length and the options say everything a
    // learner needs here. The drill above keeps its line because a drill is NOT
    // an ordinary round, and nothing else on the screen says so.
    return '';
  }

  function renderSetup(): void {
    keys = null;
    hush();
    // Held onto so picking a length can rewrite the promise in place. Repainting
    // the whole screen would drop focus off the button that was just pressed.
    const text = blurbText();
    const blurb = text ? el('p.blurb.meta', {}, text) : null;
    const setup = focusSetup(focus, pool, len, t('quiz.names.setup.lacking'));
    len = setup.len;
    wrap.replaceChildren(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.setup',
        {},
        el('h1', {}, t('common.mode.names')),
        blurb,
        setup.note,
        setup.empty
          ? null
          : lengthField(
              len,
              (n) => {
                len = n;
                if (blurb) blurb.textContent = blurbText();
              },
              setup.lengths,
            ),
        // Not in a drill: that round is built from the birds this learner keeps
        // confusing, which are by definition birds they have already met.
        drill || setup.empty ? null : newBirdsField(share, (v) => (share = v)),
        el(
          'div.field',
          {},
          el('div.prompt.label', {}, t('quiz.names.direction.label')),
          segmented(DIRECTIONS, direction, (v) => (direction = v)),
        ),
        el(
          'div.field',
          {},
          el('div.prompt.label', {}, t('quiz.round.format')),
          segmented(MODES, mode, (v) => (mode = v)),
        ),
        setup.empty
          ? emptyFocus(ctx)
          : el(
              'button.next.btn.btn-primary',
              { onclick: () => void start() },
              t('quiz.round.start'),
              icon('arrow-right', { size: 18 }),
            ),
      ),
    );
  }

  async function start(): Promise<void> {
    await setSetting('names.direction', direction);
    await setSetting('names.mode', mode);
    // A length that came off a focus is a fact about how many sparrows there
    // are, not a preference — remembering it would hand the next ordinary round
    // a number the learner never chose.
    if (ROUND_LENGTHS.includes(len)) await setRoundLength('names', len);
    await setNewBirdShare('names', share);
    // The daily card grades its own answer before sending the learner here; the
    // bird it named in `?done=` sits out, and the round is one question shorter.
    // A second round started from the done screen is the full length again.
    session = drill
      ? await buildNamesPairSession(direction, new Date(), Math.random, len)
      : await buildNamesSession(
          direction,
          new Date(),
          Math.random,
          firstRound ? ctx.params.done : undefined,
          len,
          pool ?? undefined,
        );
    firstRound = false;
    retryOn = await retryMisses();
    size = session.length;
    index = 0;
    scored = 0;
    missed = [];
    graded = null;
    render();
  }

  function render(): void {
    if (disposed) return;
    keys = null;
    hush();
    wrap.replaceChildren();
    if (!session.length) return renderEmpty();
    if (index >= session.length) return renderDone();
    if (mode === 'flip') renderFlip(session[index]);
    else renderChoice(session[index]);
  }

  function header(prompt: string): HTMLElement {
    const head = quizHeader(ctx, index, session.length, prompt);
    if (focus) tagSlot(head).append(focusTag(focus, size));
    if (index >= size) tagSlot(head).append(retryTag());
    return head;
  }

  /**
   * Persist the grade, award XP, keep the streak alive.
   *
   * `picked` is the species the learner chose, and only the choice mode has
   * one — a flip card is graded against the learner's own memory, so there is
   * no wrong bird to name and the review is logged without one.
   */
  async function grade(q: NameQuestion, rating: Rating, picked?: string): Promise<void> {
    const code = q.target.code;
    const recalled = rating !== Rating.Again;
    requeued = !recalled && retryOn;
    if (requeued) session.push(reaskName(q));
    if (index >= size) {
      // A comeback: nothing to record, and nothing for the undo to reach.
      graded = null;
      clearUndo();
      return;
    }
    if (recalled) scored += 1;
    else missed.push({ code });
    graded = recalled;
    await gradeAnswer({
      code,
      skill: 'names',
      rating,
      picked,
      xp: recalled ? XP_PER_CORRECT : 0,
    });
  }

  /**
   * Undo whatever `grade` last did, round tally included.
   *
   * The core half of this is in core/undo.ts; what belongs here is the two
   * numbers the scorecard is built from, which are counted as the round goes
   * rather than read back out of the review log.
   */
  async function takeBack(): Promise<boolean> {
    if (graded === null || !(await undoAnswer())) return false;
    if (graded) scored -= 1;
    else missed.pop();
    if (requeued) session.pop();
    requeued = false;
    graded = null;
    haptic('undo');
    return true;
  }

  function renderChoice(q: NameQuestion): void {
    const toSci = q.direction === 'toSci';
    const head = header(
      toSci ? t('quiz.round.nameInLatin') : t('quiz.round.nameTheBird'),
    );
    const choices = el('div.choices', {});
    let answered = false;
    const buttons: HTMLButtonElement[] = [];

    for (const code of q.choices) {
      const text = choiceText(code, q.direction);
      const btn = el(
        'button.choice.btn.btn-quiet',
        {},
        // Scientific names are set in italic whichever side they appear on.
        el(toSci ? 'div.nm.it' : 'div.nm', {}, text),
      ) as HTMLButtonElement;
      buttons.push(btn);
      btn.addEventListener('click', () => void settle(code, btn));
      choices.append(btn);
    }

    const nextBtn = el(
      'button.next.btn.btn-primary',
      {},
      t('quiz.round.next'),
      icon('arrow-right', { size: 18 }),
    ) as HTMLButtonElement;
    nextBtn.hidden = true;
    nextBtn.addEventListener('click', () => {
      clearUndo();
      graded = null;
      index += 1;
      render();
    });

    const undoBtn = undoButton(async () => {
      if (!answered || !(await takeBack())) return;
      render();
    });

    // No preferred facet: a Name Match question is about the names, so the
    // side-by-side opens on whatever the two birds can both show.
    const compare = compareButton();

    const dontKnow = dontKnowButton(() => settle(null, null));

    const actions: KeyActions = {
      choices: buttons,
      next: nextBtn,
      undo: undoBtn,
      skip: dontKnow,
    };
    keys = actions;

    /**
     * Everything answering does, whether a name was chosen or not.
     *
     * `code` is null for "I don't know" — `markChoices` takes -1 for the pick
     * and paints the answer with nothing marked wrong, because nothing was.
     */
    async function settle(code: string | null, btn: HTMLButtonElement | null): Promise<void> {
      if (answered) return;
      answered = true;
      const correct = code === q.target.code;
      // A made-up name is a wrong answer with no bird behind it: nothing to
      // compare against, and nothing for the mix-up record to learn from.
      const fake = code !== null && isFakeName(code);
      const named = fake ? null : code;
      haptic(correct ? 'right' : 'wrong');
      markChoices(buttons, q.choices, btn ? buttons.indexOf(btn) : -1, q.target.code);
      // The bar has to count the question just answered, or the last question of
      // a round sits at 90% while its result is on screen.
      fillProgress(head, index, session.length);
      dontKnow.hidden = true;
      if (fake && btn) {
        // Said outright, so the learner knows it was a decoy and not a real
        // bird whose other name they have yet to meet.
        btn.append(el('div.was', {}, t('quiz.round.madeUp')));
      } else if (!correct && code && btn) {
        // Name the bird they actually picked — a wrong answer is still a pair
        // worth learning, and it explains what they confused it with.
        const picked = getSpecies(code);
        if (picked) {
          btn.append(
            el(toSci ? 'div.was' : 'div.was.it', {}, promptText(picked, q.direction)),
          );
        }
      }
      // What the name means, under whichever half of the screen is showing the
      // binomial: the answer button in the common → scientific direction, the
      // prompt card in the other. Only now — "blue-winged" over four teal would
      // have answered the question.
      const etym = etymologyLine(q.target);
      if (etym) (toSci ? buttons[q.choices.indexOf(q.target.code)] : card).append(etym);
      // Nothing to hold this bird against when no bird was named, and nothing
      // for the mix-up record either: not knowing is not confusing.
      if (named) compare.offer(q.target.code, named);
      await grade(q, ratingFromCorrect(correct), named ?? undefined);
      if (!disposed) {
        nextBtn.hidden = false;
        undoBtn.hidden = graded === null;
      }
      nextBtn.replaceChildren(
        index + 1 >= session.length
          ? t('quiz.round.finish')
          : t('quiz.round.next'),
        icon('arrow-right', { size: 18 }),
      );
    }

    const card = el(
      'div.card',
      {},
      el(toSci ? 'div.front' : 'div.front.sci', {}, promptText(q.target, q.direction)),
    );
    wrap.append(
      head,
      card,
      choices,
      dontKnow,
      keyLegend(actions),
      afterBar(undoBtn, nextBtn, compare.element),
    );
  }

  function renderFlip(q: NameQuestion): void {
    const toSci = q.direction === 'toSci';
    const card = el(
      'div.card',
      {},
      el(toSci ? 'div.front' : 'div.front.sci', {}, promptText(q.target, q.direction)),
    );

    const revealBtn = el(
      'button.reveal.btn.btn-primary',
      {},
      t('quiz.names.question.reveal'),
    ) as HTMLButtonElement;
    let revealed = false;
    const gradeButtons: HTMLButtonElement[] = [];

    const reveal = (): void => {
      if (revealed) return;
      revealed = true;
      card.append(
        el('div.rule', {}),
        el(toSci ? 'div.back.sci' : 'div.back', {}, answerText(q.target, q.direction)),
      );
      // Held back until the flip in BOTH directions. Recalling the common name
      // from the binomial is the direction where the gloss gives the most away,
      // and it is the one where the binomial is on the front.
      const etym = etymologyLine(q.target);
      if (etym) card.append(etym);
      revealBtn.replaceWith(grades);
      // The four grades take over the digits the moment they appear; before
      // that, both Enter and Space flip the card.
      keys = { choices: gradeButtons };
    };
    revealBtn.addEventListener('click', reveal);
    card.addEventListener('click', reveal);

    keys = { next: revealBtn, replay: reveal };

    const grades = el('div.grades', {});
    for (const g of GRADES) {
      const btn = el(`button.btn.btn-quiet.${g.cls}`, {}, g.label) as HTMLButtonElement;
      gradeButtons.push(btn);
      btn.addEventListener('click', () => {
        for (const b of grades.children) (b as HTMLButtonElement).disabled = true;
        haptic(g.rating === Rating.Again ? 'wrong' : 'right');
        void grade(q, g.rating).then(() => {
          if (disposed) return;
          index += 1;
          render();
        });
      });
      grades.append(btn);
    }

    // Flip mode grades and moves on in one tap, which is the whole point of it
    // and leaves no answered screen to offer an undo from. So the undo waits on
    // the NEXT card instead, where it takes back the card before — the misgrade
    // it is for ("that was Easy, I hit Again") is only ever noticed once the
    // screen has already moved.
    const undoBtn = undoButton(async () => {
      if (index === 0 || graded === null || !(await takeBack())) return;
      index -= 1;
      render();
    });
    undoBtn.hidden = index === 0 || graded === null;
    // Same disc, a different sentence: here it reaches back past the card on
    // screen to the one before it, so the label has to say which answer it is
    // about. The glyph is unchanged, because the gesture is.
    undoBtn.title = t('quiz.names.question.undoTitle');
    undoBtn.setAttribute('aria-label', t('quiz.names.question.undoLabel'));

    wrap.append(
      header(toSci ? t('quiz.names.question.flipToSci') : t('quiz.names.question.flipToCommon')),
      card,
      el('div.hint', {}, t('quiz.names.question.flipHint')),
      afterBar(undoBtn, revealBtn),
    );
  }

  function renderEmpty(): void {
    wrap.append(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.done',
        {},
        el('div.big', {}, t('quiz.round.nothingToStudy')),
        el(
          'div.stat',
          {},
          drill ? t('quiz.names.empty.drill') : t('quiz.round.noSpecies'),
        ),
      ),
    );
  }

  function renderDone(): void {
    const pct = Math.round((scored / size) * 100);
    // Another round on the same settings is what a finished round almost always
    // wants next; going back through the setup screen to re-pick the direction
    // and format you just used is the rarer case, so it keeps the quiet button.
    const again = el(
      'button.next.btn.btn-primary',
      { onclick: () => void start() },
      t('quiz.round.again'),
      icon('arrow-right', { size: 18 }),
    ) as HTMLButtonElement;
    keys = { next: again };
    // The one screen in this mode that makes a sound: a name you could not
    // produce is still a bird worth hearing, so each miss plays its song where
    // it stands rather than sending the learner to its page for it.
    const panel = missedList(missed, ctx);
    missSpeakers = panel?.speakers ?? [];
    wrap.append(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.done',
        {},
        el(
          'div.big',
          {},
          mode === 'flip'
            ? t('quiz.names.done.recalled', { scored, total: size })
            : t('quiz.round.correct', { scored, total: size }),
        ),
        el('div.stat', {}, t('quiz.round.stat', { pct, xp: scored * XP_PER_CORRECT })),
        panel?.element ?? null,
        again,
        el(
          'button.next.btn.btn-quiet',
          { onclick: () => renderSetup() },
          t('quiz.round.changeOptions'),
        ),
        exitButton(ctx, Boolean(focus)),
      ),
    );
  }

  return () => {
    disposed = true;
    releaseKeys();
    hush();
    // Leaving a round mid-answer takes the undo with it: the question it
    // belonged to is off the screen, so nothing may reach back to it.
    clearUndo();
  };
};
